import { observer } from 'mobx-react';
import React from 'react';

import { AxiosError } from 'axios';

import CustomModal from 'components/CustomModal';
import { Button } from 'components/Button';
import { StyledTextField } from 'components/Input/styles';
import { CenteredCircularProgress } from 'components/Progress';

import Tooltip from 'ui/Tooltip/Tooltip';

import DropdownOptions from 'containers/FieldManager/components/DropdownOptions';
import { SortableItemType } from 'containers/FieldManager/components/DropdownOptions/types';
import {
  FieldHeader,
  FieldHeaderRow,
  ButtonsWrapper,
} from 'containers/FieldManager/components/CreateEditFieldModal/styles';
import { modifyInputValue } from 'containers/FieldManager/components/CreateEditFieldModal/types';

import API from 'utils/api';
import { APIRequest } from 'utils/api/types';
import { getErrorMessage, isBadRequest } from 'utils/api/errors';

import { UserFieldGridRow, UserFieldDropdownKind, UserFieldOption } from 'utils/api/modules/user/user.types';

import Logger from 'utils/logger';

import { useStore } from 'utils/hooks/store';

import { apiNameForRow } from './columns';
import { ApiNameInfoIcon } from './styles';

const API_NAME_TOOLTIP = 'Used to read from and write to the field from the API. Unique across your organisation.';

const optionKindFor = (key: string): UserFieldDropdownKind => key.replace('_', '-') as UserFieldDropdownKind;
const reserveNewItemPrefix = /^untitled\d+$/i;

const fieldErrorMessage = (value: string | string[] | undefined): string | undefined => {
  if (Array.isArray(value)) {
    return value.join(' ');
  }
  return value || undefined;
};

const buildSortable = (
  options: UserFieldOption[]
): { sortable: SortableItemType[]; bySlug: Map<string, UserFieldOption> } => {
  const used = new Set<string>();
  const bySlug = new Map<string, UserFieldOption>();
  const sortable = [...options]
    .sort((a, b) => a.ordering - b.ordering)
    .map((option) => {
      let base = modifyInputValue(option.name) || 'option';
      if (reserveNewItemPrefix.test(base)) {
        base = `${base}Option`;
      }
      let slug = base;
      let counter = 1;
      while (used.has(slug)) {
        slug = `${base}${counter}`;
        counter += 1;
      }
      used.add(slug);
      bySlug.set(slug, option);
      return { key: slug, value: option.name, active: option.isActive, closed: false, displayKey: slug };
    });
  return { sortable, bySlug };
};

const UserFieldEditModal: React.FunctionComponent<{
  row: UserFieldGridRow;
  onClose: (refresh: boolean) => void;
}> = ({ row, onClose }) => {
  const { session, notification } = useStore();
  const canManage = !!session.user?.showObjectManager;

  const isDropdown = row.kind === 'dropdown';

  const [label, setLabel] = React.useState(row.label);
  const [labelError, setLabelError] = React.useState<string | undefined>();
  const [options, setOptions] = React.useState<SortableItemType[]>([]);
  const originalBySlug = React.useRef<Map<string, UserFieldOption>>(new Map());
  const [optionsError, setOptionsError] = React.useState<string | undefined>();
  const [loading, setLoading] = React.useState(isDropdown);
  const [saving, setSaving] = React.useState(false);

  const refreshBaseline = React.useCallback(async (): Promise<Map<string, UserFieldOption>> => {
    if (row.kind !== 'dropdown') {
      return new Map();
    }
    const response = await API.user.listUserFieldOptions(optionKindFor(row.key), { include_inactive: true })();
    const { bySlug } = buildSortable(response.data);
    originalBySlug.current = bySlug;
    return bySlug;
  }, [row]);

  React.useEffect(() => {
    if (row.kind !== 'dropdown') {
      return undefined;
    }
    let active = true;
    API.user
      .listUserFieldOptions(optionKindFor(row.key), { include_inactive: true })()
      .then((response) => {
        if (!active) {
          return;
        }
        const { sortable, bySlug } = buildSortable(response.data);
        originalBySlug.current = bySlug;
        setOptions(sortable);
      })
      .catch(() => {
        if (active) {
          notification.enqueueErrorSnackbar('Could not load options');
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  const saveCustom = async (): Promise<void> => {
    if (row.kind !== 'custom') {
      return;
    }
    if (!label.trim()) {
      setLabelError('Label cannot be blank.');
      return;
    }
    try {
      setSaving(true);
      await API.user.updateUserFieldGridRow({ kind: 'custom', slot: row.slot, label })();
      notification.enqueueSuccessSnackbar('User field updated successfully!');
      onClose(true);
    } catch (e) {
      const error = e as AxiosError;
      if (isBadRequest(error)) {
        const data = error.response?.data as { label?: string | string[] } | undefined;
        setLabelError(fieldErrorMessage(data?.label) ?? 'Could not update field label.');
      } else {
        notification.enqueueErrorSnackbar(getErrorMessage(error, 'Could not update field label'));
        Logger.error('Invalid update user field label API response', e);
      }
    } finally {
      setSaving(false);
    }
  };

  const saveDropdown = async (): Promise<void> => {
    if (row.kind !== 'dropdown') {
      return;
    }
    const kind = optionKindFor(row.key);
    const requests: APIRequest<unknown>[] = [];

    options.forEach((item, index) => {
      const original = originalBySlug.current.get(item.key);
      if (!original) {
        const create = API.user.createUserFieldOption(kind, { name: item.value, ordering: index });
        requests.push(create as APIRequest<unknown>);
        return;
      }
      const patch: { name?: string; ordering?: number; isActive?: boolean } = {};
      if (item.value !== original.name) {
        patch.name = item.value;
      }
      if (item.active !== original.isActive) {
        patch.isActive = item.active;
      }
      if (index !== original.ordering) {
        patch.ordering = index;
      }
      if (Object.keys(patch).length > 0) {
        requests.push(API.user.updateUserFieldOption(kind, original.uuid, patch) as APIRequest<unknown>);
      }
    });

    if (requests.length === 0) {
      onClose(false);
      return;
    }

    setSaving(true);
    setOptionsError(undefined);
    const results = await Promise.allSettled(requests.map((request) => request()));
    const failure = results.find((result): result is PromiseRejectedResult => result.status === 'rejected');

    if (!failure) {
      setSaving(false);
      notification.enqueueSuccessSnackbar('User field updated successfully!');
      onClose(true);
      return;
    }

    try {
      await refreshBaseline();
    } catch {
      // noop
    }

    const error = failure.reason as AxiosError;
    if (isBadRequest(error)) {
      const data = error.response?.data as { name?: string | string[] } | undefined;
      setOptionsError(fieldErrorMessage(data?.name) ?? 'Could not save options. Please check for duplicate names.');
    } else {
      notification.enqueueErrorSnackbar(getErrorMessage(error, 'Could not save options'));
      Logger.error('Invalid save user field options API response', error);
    }
    setSaving(false);
  };

  return (
    <CustomModal openModal onClose={() => onClose(false)} modalTitle={`Edit ${row.label}`}>
      <FieldHeader>Field label</FieldHeader>
      {isDropdown ? (
        <StyledTextField fullWidth value={row.label} disabled />
      ) : (
        <StyledTextField
          fullWidth
          value={label}
          disabled={!canManage || saving}
          error={labelError !== undefined}
          helperText={labelError ?? ''}
          onChange={(event) => {
            setLabel(event.target.value);
            setLabelError(undefined);
          }}
        />
      )}

      <FieldHeaderRow>
        <FieldHeader>API name</FieldHeader>
        <Tooltip title={API_NAME_TOOLTIP} placement="top">
          <ApiNameInfoIcon aria-label="API name info" />
        </Tooltip>
      </FieldHeaderRow>
      <StyledTextField fullWidth value={apiNameForRow(row)} disabled />

      <FieldHeader>Field type</FieldHeader>
      <StyledTextField fullWidth value={isDropdown ? 'Dropdown' : 'Text'} disabled />

      {isDropdown &&
        (loading ? (
          <CenteredCircularProgress />
        ) : (
          <DropdownOptions
            editView
            options={options}
            setOptions={setOptions}
            multipleAnswers={false}
            setMultipleAnswers={() => undefined}
            errors={optionsError}
            clearError={() => setOptionsError(undefined)}
          />
        ))}

      <ButtonsWrapper>
        <Button variant="outlined" size="large" onClick={() => onClose(false)} disabled={saving}>
          Cancel
        </Button>
        {canManage && (
          <Button
            variant="contained"
            size="large"
            onClick={() => (isDropdown ? saveDropdown() : saveCustom())}
            disabled={saving || loading}
          >
            Save
          </Button>
        )}
      </ButtonsWrapper>
    </CustomModal>
  );
};

export default observer(UserFieldEditModal);
